/**
 * 清溪天成模版 - 首页
 * 
 * 设计：大圆环装饰Hero + 悬浮搜索 + 圆形分类图标 + 交错排列列表 + 全宽CTA
 * 与所有其他模板完全不同的排版：
 * - Hero区使用大型同心圆环装饰（其他模板都是渐变背景）
 * - 分类使用圆形图标排列（其他模板多用矩形卡片）
 * - 信息列表采用左右交错布局（其他模板多为均一网格）
 * - 底部CTA全宽水波纹渐变
 */

import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/templates/crystal/components/Navbar";
import Footer from "@/templates/crystal/components/Footer";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getSiteCategories, getNavCategories, getListings } from "@/utils/api";

// 用于推断分类 Icon
function inferCategoryIcon(cat: any): string {
  const name = (cat.name || "").toLowerCase();
  if (name.includes("房") || name.includes("house")) return "ri-home-4-line";
  if (name.includes("车") || name.includes("car")) return "ri-car-line";
  if (name.includes("手机") || name.includes("数码") || name.includes("电子")) return "ri-smartphone-line";
  if (name.includes("招聘") || name.includes("求职") || name.includes("工作")) return "ri-briefcase-line";
  if (name.includes("宠物") || name.includes("animal")) return "ri-heart-pulse-line";
  if (name.includes("二手") || name.includes("闲置")) return "ri-recycle-line";
  if (name.includes("美食") || name.includes("餐饮") || name.includes("food")) return "ri-restaurant-line";
  if (name.includes("教育") || name.includes("培训")) return "ri-book-open-line";
  if (name.includes("美容") || name.includes("健康")) return "ri-mental-health-line";
  return "ri-apps-2-line";
}

export default function CrystalHomePage() {
  const { siteConfig } = useSiteConfig();
  const siteName = siteConfig?.site_name || siteConfig?.name || "";

  const [categories, setCategories] = useState<any[]>([]);
  const [featuredListings, setFeaturedListings] = useState<any[]>([]);
  const [latestListings, setLatestListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    Promise.all([
      getSiteCategories().then((data) => setCategories(Array.isArray(data) ? data : [])).catch(() => {}),
      getListings({ page: 1, pageSize: 8, sort: "is_top desc,created_at desc", listingType: "featured" })
        .then((res: any) => setFeaturedListings(res?.list || res?.data?.list || []))
        .catch(() => {}),
      getListings({ page: 1, pageSize: 6, sort: "created_at desc" })
        .then((res: any) => setLatestListings(res?.list || res?.data?.list || []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/category?keyword=${encodeURIComponent(searchQuery.trim())}`;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* ═══ Hero 区域：大圆环装饰 + 悬浮搜索 ═══ */}
      <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 overflow-hidden bg-gradient-to-b from-cyan-50/60 via-white to-white">
        {/* 装饰圆环 */}
        <div className="absolute inset-0 pointer-events-none">
          {/* 大圆环 */}
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full border border-cyan-200/40" />
          <div className="absolute -top-10 -right-10 w-[400px] h-[400px] rounded-full border border-cyan-200/30" />
          <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full border border-cyan-200/20" />
          {/* 左边小圆环 */}
          <div className="absolute top-1/4 -left-16 w-48 h-48 rounded-full border border-blue-200/30" />
          <div className="absolute top-1/4 + 6 -left-8 w-32 h-32 rounded-full border border-blue-200/20" />
          {/* 浮动小点 */}
          <div className="absolute top-1/3 left-1/4 w-3 h-3 rounded-full bg-cyan-300/40" />
          <div className="absolute bottom-1/4 right-1/3 w-2 h-2 rounded-full bg-blue-400/40" />
          <div className="absolute top-1/2 left-1/2 w-4 h-4 rounded-full bg-cyan-400/20 blur-sm" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-cyan-50 border border-cyan-100 rounded-full text-xs font-semibold text-cyan-600 mb-6">
            <i className="ri-flashlight-line" />
            全新升级，更好的体验
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            发现<span className="bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">身边</span>的美好
          </h1>
          <p className="text-base md:text-lg text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            {siteName ? `${siteName} — ` : ""}汇集本地分类信息，找工作、找房源、淘二手，一键触达
          </p>

          {/* 悬浮搜索框 - 毛玻璃效果 */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl shadow-cyan-500/10 border border-cyan-100/50 p-1.5 flex items-center">
                <i className="ri-search-line text-gray-400 text-lg pl-4" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索你想找的信息..."
                  className="flex-1 px-4 py-3 bg-transparent text-gray-700 placeholder-gray-400 outline-none text-sm"
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-2xl hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md shadow-cyan-500/25 text-sm"
                >
                  搜索
                </button>
              </div>
            </div>
          </form>

          {/* 热门搜索 */}
          <div className="flex items-center justify-center gap-2 mt-5 flex-wrap">
            <span className="text-xs text-gray-400">热门：</span>
            {["招聘", "二手房", "二手车", "宠物", "兼职"].map((tag) => (
              <Link
                key={tag}
                to={`/category?keyword=${tag}`}
                className="px-3 py-1 bg-gray-50 hover:bg-cyan-50 text-xs text-gray-500 hover:text-cyan-600 rounded-full border border-gray-100 hover:border-cyan-200 transition-all"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ 分类区域：圆形图标排列 ═══ */}
      <section className="relative -mt-8 z-10 pb-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-3xl shadow-xl shadow-cyan-500/5 border border-cyan-100/30 p-6 md:p-10">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-gradient-to-b from-cyan-400 to-blue-600" />
                全部分类
              </h2>
              <Link to="/category" className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line text-xs" />
              </Link>
            </div>

            {loading ? (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="flex flex-col items-center gap-3 animate-pulse">
                    <div className="w-14 h-14 rounded-full bg-gray-100" />
                    <div className="w-10 h-3 bg-gray-100 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-4 md:gap-5">
                {categories.slice(0, 8).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-2.5"
                  >
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-100 flex items-center justify-center group-hover:shadow-lg group-hover:shadow-cyan-200/40 group-hover:border-cyan-300 group-hover:scale-110 transition-all duration-300">
                      <i className={`${inferCategoryIcon(cat)} text-xl md:text-2xl text-cyan-600 group-hover:text-blue-600 transition-colors`} />
                    </div>
                    <span className="text-xs text-gray-600 group-hover:text-gray-900 font-medium transition-colors text-center leading-tight">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ═══ 推荐位：横向大卡片 ═══ */}
      {featuredListings.length > 0 && (
        <section className="py-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-gradient-to-b from-cyan-400 to-blue-600" />
                精选推荐
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featuredListings.slice(0, 4).map((item, idx) => (
                <Link
                  key={item.id}
                  to={`/listing/${item.id}`}
                  className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-cyan-200 hover:shadow-xl hover:shadow-cyan-500/5 transition-all duration-300"
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-cyan-100/40 to-blue-50/40 overflow-hidden relative">
                    {item.images?.[0] ? (
                      <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-4xl text-cyan-200" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-cyan-500/90 backdrop-blur-sm text-white text-xs rounded-full font-medium">
                        {item.category_name || "推荐"}
                      </span>
                    </div>
                    {item.price && (
                      <div className="absolute bottom-3 right-3">
                        <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-cyan-600 text-sm font-bold rounded-full shadow-sm">
                          ¥{item.price}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 group-hover:text-cyan-600 transition-colors line-clamp-2 text-sm mb-2">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-1.5 text-xs text-gray-400">
                      <i className="ri-map-pin-line text-cyan-400" />
                      {item.city_name || item.district || "未知位置"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ 最新信息：左右交错布局 ═══ */}
      <section className="py-10 bg-cyan-50/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-gray-800 flex items-center justify-center gap-2">
              <span className="w-1 h-5 rounded-full bg-gradient-to-b from-cyan-400 to-blue-600" />
              最新发布
            </h2>
          </div>

          {loading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse flex gap-4 p-4 bg-white/80 rounded-2xl">
                  <div className="w-24 h-24 bg-gray-100 rounded-2xl shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {latestListings.map((item, idx) => {
                const isEven = idx % 2 === 0;
                return (
                  <Link
                    key={item.id}
                    to={`/listing/${item.id}`}
                    className={`flex flex-col sm:flex-row ${isEven ? "" : "sm:flex-row-reverse"} items-stretch gap-4 p-3 bg-white rounded-2xl border border-gray-100 hover:border-cyan-200 hover:shadow-lg hover:shadow-cyan-500/5 transition-all duration-300 group`}
                  >
                    {/* 图片 */}
                    <div className={`sm:w-40 h-32 sm:h-auto rounded-xl overflow-hidden bg-gradient-to-br ${isEven ? "from-cyan-100/40 to-blue-50/40" : "from-blue-100/40 to-cyan-50/40"} shrink-0`}>
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className={`ri-image-line text-3xl ${isEven ? "text-cyan-200" : "text-blue-200"}`} />
                        </div>
                      )}
                    </div>
                    {/* 内容 */}
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${isEven ? "bg-cyan-50 text-cyan-600" : "bg-blue-50 text-blue-600"}`}>
                            {item.category_name || "信息"}
                          </span>
                          {item.is_top === 1 && (
                            <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-red-50 text-red-500">置顶</span>
                          )}
                        </div>
                        <h3 className="font-semibold text-gray-800 group-hover:text-cyan-600 transition-colors line-clamp-1 text-sm mb-1">
                          {item.title}
                        </h3>
                        <p className="text-xs text-gray-400 line-clamp-1">{item.description || item.location}</p>
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line text-cyan-400" />
                          {item.city_name || item.district || "未知位置"}
                        </span>
                        <span className="flex items-center gap-3">
                          <span className="flex items-center gap-1"><i className="ri-eye-line" /> {item.views || 0}</span>
                          <span>{item.created_at ? new Date(item.created_at).toLocaleDateString("zh-CN") : ""}</span>
                        </span>
                      </div>
                      {item.price && (
                        <div className="mt-2 text-right">
                          <span className="text-base font-bold text-cyan-600">¥{item.price}</span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="text-center mt-8">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 px-6 py-2.5 border-2 border-cyan-200 text-cyan-600 font-semibold rounded-full hover:bg-cyan-50 transition-all text-sm"
            >
              浏览更多信息 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ CTA 区域：全宽水波纹渐变 ═══ */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-600 opacity-90" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #fff 0%, transparent 50%), radial-gradient(circle at 80% 50%, #fff 0%, transparent 50%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-4">免费发布您的第一条信息</h2>
          <p className="text-cyan-100 text-base mb-8">3分钟快速发布，让更多人看到您的内容</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/publish"
              className="px-8 py-3.5 bg-white text-cyan-600 font-bold rounded-full hover:bg-cyan-50 transition-all shadow-lg shadow-cyan-700/30 text-sm"
            >
              立即发布
            </Link>
            <Link
              to="/register"
              className="px-8 py-3.5 border-2 border-white/60 text-white font-bold rounded-full hover:bg-white/10 transition-all text-sm"
            >
              免费注册
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
